// Mijn functies (Gebruiker; PLAN-W28; FRONTEND-BLUEPRINT §3.8). Read-only list of the signed-in user's
// active role assignments across organisations. Talks to the auth-worker (AUTH_BASE) credentialed — the
// role-assignment registry is owner-only — so it works behind real auth. The canonical Functie/RACI layer
// is content-gated; this surfaces the platform role (standardRoleCode), honestly labelled.
import { useQuery } from "@tanstack/react-query";
import { useI18n } from "../../lib/i18n";
import { AUTH_BASE } from "../../lib/env";
import { PageHeader } from "../../components/page-header";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

interface Assignment {
  assignmentId: string; tenantId: string; organisationName: string;
  standardRoleCode: string | null; scopeKind: string; status: string;
  effectiveFrom: string; effectiveTo: string | null;
}

function roleLabel(code: string | null): string {
  if (!code) return "—";
  return code.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function MijnFunctiesRoute() {
  const { t } = useI18n();
  const { data, isLoading, error } = useQuery({
    queryKey: ["me-functies"],
    queryFn: async () => {
      const res = await fetch(`${AUTH_BASE}/me/functies`, { credentials: "include", headers: { "content-type": "application/json" } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as Assignment[];
    },
  });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t("functies.title")} description={t("functies.sub")} />
      {isLoading && <p className="text-sm text-muted-foreground">{t("common.loading")}</p>}
      {error && <p className="text-sm text-destructive">{(error as Error).message}</p>}
      {data && data.length === 0 && <p className="text-sm text-muted-foreground">{t("functies.empty")}</p>}
      {data && data.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("functies.org")}</TableHead>
              <TableHead>{t("functies.role")}</TableHead>
              <TableHead>{t("functies.scope")}</TableHead>
              <TableHead>{t("functies.status")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((a) => (
              <TableRow key={a.assignmentId}>
                <TableCell className="font-medium">{a.organisationName}</TableCell>
                <TableCell>{roleLabel(a.standardRoleCode)}</TableCell>
                <TableCell className="text-muted-foreground">{a.scopeKind.replace(/_/g, " ")}</TableCell>
                <TableCell><Badge variant="secondary">{a.status}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <p className="text-xs text-muted-foreground">{t("functies.note")}</p>
    </div>
  );
}
